import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { get } from 'lodash';
import { getCreditExchangeInfo, getMentorCreditTransactions } from 'libs/api/user';
import { useDataApi, usePagination } from 'libs/hooks';
import { useTimezone } from 'libs/hooks';
import { commifyFormatter } from 'libs/validators';
import { TListResponse, TPagination } from 'modules/types';
import { TCreditTransaction, TCreditExchangeInfo } from 'modules/user';
import { CWindowWidth, CDateFormat } from 'constants/base.const';
import { EExchangeHistoryStatus } from 'enums/mentoring.enum';
import { ERoute } from 'enums/route.enum';
import { Tooltip, Spin, List, Button, Tabs, Col, Row, Typography } from 'antd';
import styled from 'styled-components';
import { PrimaryColor, SecondaryColor } from 'GlobalStyles';
import BackgroundSource from 'assets/images/banners/5.webp';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
export const UserCreditExchangeWrapper = styled.div`
  .ant-typography {
    color: #000;
  }
  .content-title {
    font-size: 16px;
    font-weight: bold;
    color: #000;
    margin-bottom: 8px;
  }
  .ant-upload-list {
    width: 100%;
  }
  .content-box {
    border-radius: 8px;
    width: 100%;
    min-height: 100px;
    .ant-row {
      min-height: 100px;
    }
    padding: 8px;
    background-image: url(${BackgroundSource});
    background-size: cover;
    color: #fff;
    text-align: center;
    .large-text {
      font-size: 28px;
      font-weight: bold;
    }
    .small-text {
      font-size: 12px;
    }
    .absolute-btn {
      position: absolute;
      right: 8px;
      padding: 5px !important;
    }
  }
  .file-placeholder {
    color: #adadad;
    left: 11px;
    position: absolute;
  }
  .form-normal .ant-form-item-label label {
    font-weight: normal;
  }
`;

const UserCreditExchange = () => {
  const [{ data: exchangeData, loading: exchangeLoading }] = useDataApi<TCreditExchangeInfo>(
    getCreditExchangeInfo.bind(null),
  );
  const [pagination, setPagination] = usePagination(8);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
  };
  const getPromise = getMentorCreditTransactions.bind(null, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: pagination.q,
    },
  });
  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TCreditTransaction>>(getPromise, {
    contents: [],
    last: false,
  });
  const [getDateTime, timezone] = useTimezone();

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  return (
    <UserCreditExchangeWrapper>
      <div
        style={{
          maxWidth: `${CWindowWidth - 400}px`,
          margin: 'auto',
          padding: '40px 10px 0px 10px',
        }}
      >
        <Title level={2}>정산하기</Title>
      </div>
      <div
        style={{
          maxWidth: `${CWindowWidth - 400}px`,
          padding: '30px 10px',
          margin: 'auto',
        }}
      >
        <Row gutter={[32, 16]} justify='space-between'>
          <Col
            xs={{ span: 24, order: 2 }}
            sm={{ span: 24, order: 2 }}
            md={{ span: 15, order: 1 }}
            lg={{ span: 15, order: 1 }}
            xl={{ span: 16, order: 1 }}
          >
            <div className='content-title'>정산가능 멘토링</div>
            <div className='content-box' style={{ backgroundColor: '#2a4753' }}>
              <Spin tip='loading...' spinning={exchangeLoading} delay={1000}>
                <Link to={ERoute.UserCreditExchangeRequest}>
                  <Button className='absolute-btn' style={{ zIndex: 10 }}>
                    정산신청
                  </Button>
                </Link>
                <Row justify='space-between' style={{ maxWidth: '500px', margin: 'auto' }} align='middle'>
                  <Col xs={24} sm={24} md={10} lg={10} xl={10}>
                    <div className='large-text'>
                      {commifyFormatter(String(get(exchangeData, 'exchangeable_amount')))}원
                    </div>
                    <div className='small-text'>정산 예상 금액</div>
                  </Col>
                  <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                    <div className='large-text'>
                      {commifyFormatter(String(get(exchangeData, 'exchangeable_count')))}회
                    </div>
                    <div className='small-text'>멘토링 횟수</div>
                  </Col>
                </Row>
              </Spin>
            </div>
            <div className='content-title' style={{ marginTop: '20px' }}>
              정산 내역
            </div>
            <div className='content-box' style={{ backgroundColor: `${SecondaryColor}` }}>
              <Row justify='space-between' style={{ maxWidth: '500px', margin: 'auto' }} align='middle'>
                <Col xs={24} sm={24} md={10} lg={10} xl={10}>
                  <div className='large-text'>
                    {commifyFormatter(String(get(exchangeData, 'exchanged_amount')))}원
                  </div>
                  <div className='small-text'>정산 완료 금액</div>
                </Col>
                <Col xs={24} sm={24} md={14} lg={14} xl={14}>
                  <div className='large-text'>
                    {commifyFormatter(String(get(exchangeData, 'exchanged_count')))}회
                  </div>
                  <div className='small-text'>멘토링 횟수</div>
                </Col>
              </Row>
            </div>
            <div className='content-title' style={{ marginTop: '30px' }}>
              멘토링 정산 내역
              <div
                style={{
                  color: '#aeaeae',
                  fontSize: '12px',
                  fontWeight: 'normal',
                }}
              >
                *멘토링 후 피드백 제출이 완료되어야 적립이 확정됩니다.
              </div>
            </div>
            <List
              pagination={{
                ...pagination,
                total: get(data, 'total', 0),
                position: 'bottom',
                hideOnSinglePage: true,
                onChange: (page) => {
                  reloadPage({
                    current: page,
                    pageSize: pagination.pageSize,
                  });
                },
              }}
              loading={loading}
              dataSource={get(data, 'contents')}
              renderItem={(item) => (
                <div style={{ fontSize: '14px', color: '#000' }}>
                  {get(item, 'status') === EExchangeHistoryStatus.Rejected && (
                    <Tooltip
                      placement='topLeft'
                      title={<span style={{ fontWeight: 'bold' }}>{get(item, 'memo')}</span>}
                    >
                      <div style={{ margin: '15px 0px' }}>
                        <span style={{ color: '#ff0000', fontWeight: 'bold' }}>거절</span>
                        <span>
                          &nbsp;&nbsp;&nbsp;&nbsp;
                          {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                        </span>
                        <span>&nbsp;&nbsp;&nbsp;-{String(get(item, 'credit'))}회</span>
                      </div>
                    </Tooltip>
                  )}
                  {get(item, 'status') === EExchangeHistoryStatus.Accepted && (
                    <div style={{ margin: '15px 0px' }}>
                      <span
                        style={{
                          color: `${SecondaryColor}`,
                          fontWeight: 'bold',
                        }}
                      >
                        정산
                      </span>
                      <span>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                      </span>
                      <span>&nbsp;&nbsp;&nbsp;-{String(get(item, 'credit'))}회</span>
                    </div>
                  )}
                  {get(item, 'status') === EExchangeHistoryStatus.Pending && (
                    <div style={{ margin: '15px 0px' }}>
                      <span style={{ color: '#696969', fontWeight: 'bold' }}>요청</span>
                      <span>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                      </span>
                      <span>&nbsp;&nbsp;&nbsp;-{String(get(item, 'credit'))}회</span>
                    </div>
                  )}
                  {get(item, 'status') === EExchangeHistoryStatus.Deposit && (
                    <Tooltip
                      title={
                        <Link
                          to={`${ERoute.MentoringClassView.replace(
                            ':record_id',
                            String(get(item, 'mentoring_class_id')),
                          )}`}
                        >
                          <span style={{ color: '#fff', fontWeight: 'bold' }}>멘토링 자세히보기</span>
                        </Link>
                      }
                      placement='topLeft'
                    >
                      <div style={{ margin: '15px 0px' }}>
                        <span
                          style={{
                            color: `${PrimaryColor}`,
                            fontWeight: 'bold',
                          }}
                        >
                          적립
                        </span>
                        <span>
                          &nbsp;&nbsp;&nbsp;&nbsp;
                          {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                        </span>
                        <span>&nbsp;&nbsp;&nbsp;+{String(get(item, 'credit'))}회</span>
                        <span style={{ float: 'right' }}>{get(item, 'with_mentee')} 멘티</span>
                      </div>
                    </Tooltip>
                  )}
                  {get(item, 'status') === EExchangeHistoryStatus.NeedFeedback && (
                    <Tooltip
                      title={
                        <span>
                          피드백을 작성해야 정산 가능합니다.
                          <div>
                            <Link
                              to={`${ERoute.MentoringClassView.replace(
                                ':record_id',
                                String(get(item, 'mentoring_class_id')),
                              )}`}
                            >
                              <span style={{ color: '#fff', fontWeight: 'bold' }}>멘토링 자세히보기</span>
                            </Link>
                          </div>
                        </span>
                      }
                      placement='topLeft'
                    >
                      <div style={{ margin: '15px 0px' }}>
                        <span style={{ color: '#ADADAD', fontWeight: 'bold' }}>적립</span>
                        <span>
                          &nbsp;&nbsp;&nbsp;&nbsp;
                          {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                        </span>
                        <span>&nbsp;&nbsp;&nbsp;+{String(get(item, 'credit'))}회</span>
                        <span style={{ float: 'right' }}>{get(item, 'with_mentee')} 멘티</span>
                      </div>
                    </Tooltip>
                  )}
                </div>
              )}
            />
          </Col>
          <Col
            xs={{ span: 24, order: 1 }}
            sm={{ span: 24, order: 1 }}
            md={{ span: 8 }}
            lg={{ span: 8 }}
            xl={{ span: 7 }}
            style={{ textAlign: 'center' }}
          >
            <div className='border-box hide-on-mobile' style={{ textAlign: 'left' }}>
              <Title level={4}>정산은 어떻게 하나요?</Title>
              <Text style={{ fontSize: '13px' }}>
                정산 신청하시면 영업일 14일 내에 Career Step 검토 및 입금이 완료됩니다.
              </Text>
            </div>
          </Col>
        </Row>
      </div>
    </UserCreditExchangeWrapper>
  );
};

export default UserCreditExchange;
